/**
 * Mobile Chat Drawer Component
 *
 * Bottom drawer for AI chat on narrow screens (< lg breakpoint).
 * Features:
 * - Persistent Drawer from bottom (doesn't block scrolling)
 * - User can scroll note content above the drawer
 * - Drag-to-resize functionality
 * - Reuses SidebarContainer for Chat/Audit tabs
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { EditIcon } from '@icons/EditIcon';
import {
  ChevronRight as ChevronRightIcon,
  DragHandle as DragHandleIcon,
} from '@mui/icons-material';
import { Box, Drawer, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { MOBILE_DRAWER_DIMENSIONS } from '../constants/layout';

import { ModeToggle } from './ModeToggle';
import type { SidebarMode } from './SidebarContainer';

export interface MobileChatDrawerProps {
  /**
   * Whether the drawer is open
   */
  open: boolean;

  /**
   * Callback when drawer should close
   */
  onClose: () => void;

  /**
   * Current drawer height as percentage of viewport height
   */
  drawerHeight: number;

  /**
   * Callback when drawer height changes (via drag)
   */
  onHeightChange: (height: number) => void;

  /**
   * Current sidebar mode (chat or audit)
   */
  mode: SidebarMode;

  /**
   * Callback when mode changes
   */
  onModeChange: (mode: SidebarMode) => void;

  /**
   * Whether to show the toggle (feature flag controlled)
   */
  showToggle?: boolean;

  /**
   * Number of audit issues to display as badge
   */
  auditIssueCount?: number;

  /**
   * Whether there are chat messages
   */
  hasMessages?: boolean;

  /**
   * Callback when clear chat is requested
   */
  onClearChat?: () => void;

  /**
   * Content to render for chat mode
   */
  chatContent: React.ReactNode;

  /**
   * Content to render for audit mode
   */
  auditContent: React.ReactNode;

  /**
   * Content to render for analysis mode
   */
  analysisContent?: React.ReactNode;

  /**
   * Whether to show the analysis mode option
   */
  showAnalysisMode?: boolean;

  /**
   * Note ID for analytics tracking
   */
  noteId?: string;

  /**
   * Encounter ID for analytics tracking
   */
  encounterId?: string;
}

/**
 * MobileChatDrawer - Bottom drawer for AI chat on narrow screens
 *
 * Uses MUI Drawer with variant="persistent":
 * - Doesn't block scrolling of content behind
 * - Drag handle for resizing height
 * - Close button to hide drawer
 * - Chat/Audit mode toggle
 */
export const MobileChatDrawer: React.FC<MobileChatDrawerProps> = ({
  open,
  onClose,
  drawerHeight,
  onHeightChange,
  mode,
  onModeChange,
  showToggle = false,
  auditIssueCount = 0,
  hasMessages = false,
  onClearChat,
  chatContent,
  auditContent,
  analysisContent,
  showAnalysisMode = false,
}) => {
  const theme = useTheme();

  // Refs for drag handling
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);

  // Store refs to current handlers for cleanup
  const mouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const mouseUpRef = useRef<(() => void) | null>(null);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (mouseMoveRef.current) {
        document.removeEventListener('mousemove', mouseMoveRef.current);
      }
      if (mouseUpRef.current) {
        document.removeEventListener('mouseup', mouseUpRef.current);
      }
    };
  }, []);

  // Handle drag start
  const handleDragStart = useCallback(
    (clientY: number) => {
      isDragging.current = true;
      startY.current = clientY;
      startHeight.current = drawerHeight;
    },
    [drawerHeight],
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!isDragging.current) return;

      const deltaY = startY.current - clientY;
      const deltaVh = (deltaY / window.innerHeight) * 100;
      const newHeight = Math.min(
        MOBILE_DRAWER_DIMENSIONS.MAX_HEIGHT_VH,
        Math.max(MOBILE_DRAWER_DIMENSIONS.MIN_HEIGHT_VH, startHeight.current + deltaVh),
      );

      onHeightChange(newHeight);
    },
    [onHeightChange],
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Mouse event handlers for drag handle
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientY);

      const handleMouseMove = (e: MouseEvent) => {
        handleDragMove(e.clientY);
      };

      const handleMouseUp = () => {
        handleDragEnd();
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        mouseMoveRef.current = null;
        mouseUpRef.current = null;
      };

      mouseMoveRef.current = handleMouseMove;
      mouseUpRef.current = handleMouseUp;

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleDragStart, handleDragMove, handleDragEnd],
  );

  // Touch event handlers for drag handle
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        handleDragStart(touch.clientY);
      }
    },
    [handleDragStart],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        handleDragMove(touch.clientY);
      }
    },
    [handleDragMove],
  );

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Handle mode toggle
  const handleToggleChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMode: SidebarMode | null) => {
      if (newMode !== null) {
        onModeChange(newMode);
      }
    },
    [onModeChange],
  );

  return (
    <Drawer
      anchor="bottom"
      variant="persistent"
      open={open}
      PaperProps={{
        sx: {
          height: `${drawerHeight}vh`,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden',
          boxShadow: theme.shadows[16],
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1,
          cursor: 'ns-resize',
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <DragHandleIcon
          sx={{
            color: theme.palette.text.secondary,
            fontSize: 24,
          }}
        />
      </Box>

      {/* Drawer Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100% - ${MOBILE_DRAWER_DIMENSIONS.DRAG_HANDLE_HEIGHT}px)`, // Account for drag handle
        }}
      >
        {/* Header with close button and mode toggle */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: 48,
            px: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
            bgcolor: theme.palette.background.paper,
            gap: 1,
          }}
        >
          {/* Close Button */}
          <Tooltip title="Hide chat" placement="right">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                transform: 'rotate(90deg)', // Point down for bottom drawer
                '&:hover': {
                  color: theme.palette.text.primary,
                  bgcolor: theme.palette.action.hover,
                },
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
              aria-label="Hide chat"
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>

          {/* Toggle (center) */}
          {showToggle && (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <ModeToggle
                mode={mode}
                auditIssueCount={auditIssueCount}
                onChange={handleToggleChange}
                minWidth={90}
                size="small"
                showAnalysisMode={showAnalysisMode}
              />
            </Box>
          )}

          {/* Action Button - Show clear chat only in chat mode */}
          {mode === 'chat' ? (
            <ClearChatButton hasMessages={hasMessages} onClear={onClearChat} />
          ) : (
            <Box sx={{ width: 32, flexShrink: 0 }} />
          )}
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {mode === 'chat' ? chatContent : mode === 'audit' ? auditContent : analysisContent}
        </Box>
      </Box>
    </Drawer>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

interface ClearChatButtonProps {
  hasMessages: boolean;
  onClear?: () => void;
}

const ClearChatButton: React.FC<ClearChatButtonProps> = ({ hasMessages, onClear }) => {
  const theme = useTheme();

  return (
    <Tooltip title="Start new chat" placement="left">
      <span>
        <IconButton
          onClick={onClear}
          disabled={!hasMessages}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.primary.main,
              bgcolor: theme.palette.primary.light + '20',
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
            },
            transition: 'all 0.2s ease',
            flexShrink: 0,
          }}
          aria-label="Start new chat"
        >
          <EditIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
